import React from 'react'
import Nav from '../../../../src/components/Nav'
import { urls } from '../../../../src/utils/config'
import Footer from '../../../../src/components/Footer'
import { Amplitude, LogOnMount } from '@amplitude/react-amplitude'
import { NextSeo } from 'next-seo'
import { makeStyles } from '@material-ui/core/styles'
import CourseGrid from '../../../../src/components/Academy/Course/CourseGrid'
import ReactMarkdown from 'react-markdown'
import { Typography, Divider, Breadcrumbs, Link, Grid, Button } from '@material-ui/core'
import HelpSection from '../../../../src/components/Help/HelpSection'
import JoinCommunitySection from '../../../../src/components/Footer/JoinCommunitySection'
import CourseSideBar from '../../../../src/components/Academy/Course/CourseSideBar'

import { getCourse, getLesson, getCourses } from '../../../../src/services/academy'
import { Course as ICourse, Lesson as ILesson } from '../../../../types/academy'
import { GetServerSideProps } from 'next'
import { ArrowBack, ArrowForward } from '@material-ui/icons'

const useStyles = makeStyles((theme) => ({
  root: {
    maxWidth: '1200px',
    margin: 'auto',
  },
  contentSection: {
    margin: '0',
    padding: '20px',
  },
  title: {
    textAlign: 'left',
    letterSpacing: '-0.02em',
    margin: '2rem 0',
    [theme.breakpoints.down('sm')]: {
      fontSize: '36px',
      maxWidth: '100%',
    },
  },
  subtitle: {
    marginTop: '20px',
    marginBottom: '30px',
    position: 'relative',
    width: 'fit-content',
    '& h5': {
      color: theme.palette.primary.main,
      position: 'relative',
    },
  },
  heroContent: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  heroTextSection: {
    width: '100%',
    padding: '20px',
    [theme.breakpoints.up('md')]: {
      width: '60%',
    },
  },
  section: {},
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  divider: {
    margin: '80px 0',
  },
  breadCrumbs: {
    margin: ' 0 0 2rem 0',
    padding: '.5rem .5rem .5rem 1.5rem',
    position: 'relative',
    fontSize: '.8rem',
    '&::before': {
      position: 'absolute',
      content: '""',
      height: '100%',
      top: 0,
      left: 0,
      width: '0.5rem',
      background: theme.palette.primary.main,
      borderRadius: '0.3rem',
    },
  },
  p: {
    fontSize: '.8rem',
    fontWeight: 'bold',
  },

  sideBar: {
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  videoWrapper: {
    position: 'relative',
    height: '360px',
    overflow: 'hidden',
    borderRadius: '1rem',
    '& iframe': {
      position: 'absolute',
      width: '100%',
      height: '100%',
      top: 0,
      left: 0,
    },
  },
  navigation: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: '2rem',

    '& a p': {
      fontSize: '1rem',
      textTransform: 'none',
      fontWeight: 'bold',
      color: 'black',
    },
  },
  prevButton: {},
  nextButton: {
    marginLeft: 'auto',
  },
}))

interface Props {
  course: ICourse
  lesson: ILesson
  relatedCourses: ICourse[]
}

function Lesson({ relatedCourses, course, lesson }: Props): JSX.Element {
  const classes = useStyles({})
  const ogImage = {
    large: lesson?.sharing_image?.formats?.large?.url
      ? lesson.sharing_image.formats.large.url
      : lesson.sharing_image.url,
    small: lesson?.sharing_image?.formats?.small?.url
      ? lesson.sharing_image.formats.small.url
      : lesson.sharing_image.url,
  }

  const getLessonByNumber = (number: number) => {
    return course.lessons.find((lesson) => lesson.number === number)
  }

  const prevLesson = getLessonByNumber(lesson.number - 1)
  const nextLesson = getLessonByNumber(lesson.number + 1)

  const sortedLessons = [...course.lessons].sort((a, b) => a.number - b.number)

  const firstLesson = sortedLessons[0]

  return (
    <Amplitude
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      eventProperties={(inheritedProps: { page: any }) => ({
        ...inheritedProps,
        page: {
          ...inheritedProps.page,
          name: urls.home,
        },
      })}
    >
      {(): JSX.Element => (
        <>
          <LogOnMount eventType="page-displayed" />
          <NextSeo
            title={lesson.title}
            description={`Cryptotesters is your go-to resource to navigate the crypto universe. We help you find the best cryptocurrency exchanges, wallets and much more.`}
            openGraph={{
              url: `${process.env.NEXT_PUBLIC_APP_URL}/courses/${course.slug}/lesson/${lesson.slug}`,
              title: lesson.title,
              description:
                lesson.description ||
                `Cryptotesters is your go-to resource to navigate the crypto universe. We help you find the best cryptocurrency exchanges, wallets and much more.`,
              images: [
                {
                  url: ogImage.large,
                  width: 1200,
                  height: 630,
                  alt: `${lesson?.title} image - Cryptotesters.com`,
                },
                {
                  url: ogImage.small,
                  width: 600,
                  height: 400,
                  alt: `${lesson?.title} image - Cryptotesters.com`,
                },
              ],
              site_name: 'Cryptotesters.com',
            }}
            twitter={{
              handle: '@cryptotesters',
              site: '@cryptotesters',
              cardType: 'summary_large_image',
            }}
          />
          <Nav />
          <div className={classes.root}>
            <div className={classes.contentSection}>
              <Grid container spacing={8}>
                <Grid item md={3} className={classes.sideBar}>
                  <CourseSideBar course={course} currentLesson={lesson.slug} />
                </Grid>
                <Grid item md={8} xs={12}>
                  <div>
                    <Breadcrumbs
                      separator="->"
                      aria-label="breadcrumb"
                      className={classes.breadCrumbs}
                    >
                      <Link color="inherit" href="/courses">
                        All Courses
                      </Link>
                      <Link
                        color="inherit"
                        href={`/courses/${course.slug}/lesson/${firstLesson.slug}`}
                      >
                        {course.title}
                      </Link>
                      <Typography className={classes.p} color="textPrimary">
                        {lesson.title}
                      </Typography>
                    </Breadcrumbs>

                    <Typography variant="h1" className={classes.title}>
                      {lesson.title}
                    </Typography>
                    <div
                      className={classes.videoWrapper}
                      dangerouslySetInnerHTML={{ __html: lesson.iframe }}
                    ></div>
                    <ReactMarkdown>{lesson.content}</ReactMarkdown>
                  </div>
                </Grid>
              </Grid>
              <div className={classes.navigation}>
                {prevLesson && (
                  <Button
                    className={classes.prevButton}
                    href={`/courses/${course.slug}/lesson/${prevLesson.slug}`}
                  >
                    <ArrowBack />
                    <Typography>Previous Lesson</Typography>
                  </Button>
                )}
                {nextLesson && (
                  <Button
                    className={classes.nextButton}
                    href={`/courses/${course.slug}/lesson/${nextLesson.slug}`}
                  >
                    <Typography>Next Lesson</Typography>
                    <ArrowForward />
                  </Button>
                )}
              </div>
              <div>
                {relatedCourses.length > 0 && (
                  <div>
                    <Divider className={classes.divider} />
                    <div className={classes.sectionHeader}>
                      <Typography variant="h5"> Related Courses </Typography>
                    </div>
                    <CourseGrid courses={relatedCourses} type="details" />
                  </div>
                )}
              </div>
            </div>
          </div>
          <HelpSection />
          <JoinCommunitySection />
          <Footer />
        </>
      )}
    </Amplitude>
  )
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  const lessonSlug = context.params?.lessonSlug
  const courseSlug = context.params?.courseSlug

  try {
    const course = await getCourse(courseSlug as string)
    const lesson = await getLesson(lessonSlug as string)
    const relatedCourses = await getCourses()
    return {
      props: {
        course,
        lesson,
        relatedCourses,
      },
    }
  } catch (e) {
    console.error(e.message)
    return {
      notFound: true,
    }
  }
}

export default Lesson
